import gsap from "gsap";
import * as PIXI from "pixi.js";
import { EventHandler } from "../../util/event/EventHandler";
import { EventObject } from "../../util/event/EventObject";
import { Logger } from "../../util/log/Logger";
import { DestroyUtil } from "../../util/view/DestroyUtil";
import { SpineUtil } from "../../util/view/SpineUtil";
import { CommonGameEvent } from "../controller/event/CommonGameEvent";
import { OrientationType } from "../renderer/constant/RendererConstants";
import { AbstractControl } from "./AbstractControl";
import { SpineConstants } from "./constant/ControlConstants";
/**
 * TitanSpine.ts
 */
export class TitanSpine extends AbstractControl {
    constructor(properties, portraitProperties) {
        super(properties, portraitProperties);
        SpineUtil.addEventListener(this, (data) => {
            if (data.getAudioId()) {
                Logger.verbose("DISPATCH PLAY_SPINE_SOUND: " + data.getAudioId());
                this.dispatchCore(new EventObject(CommonGameEvent.PLAY_SPINE_SOUND.getType(), data));
            }
        });
    }
    static createSpine(...args) {
        let hasName = typeof args[1] === "string";
        let _parent, _name, _properties, _portraitProperties, _completeHandler, __caller;
        if (hasName) {
            [_parent, _name, _properties, _portraitProperties, _completeHandler, __caller] = args;
        }
        else {
            [_parent, _properties, _portraitProperties, _completeHandler, __caller] = args;
            _name = "Spine";
        }
        let spine;
        if (_properties) {
            spine = new TitanSpine(_properties, _portraitProperties);
            if (spine) {
                if (_completeHandler && __caller) {
                    spine.onComplete(_completeHandler, __caller);
                }
                if (_parent) {
                    _parent.addChild(spine);
                }
                spine.name = _name;
            }
        }
        return spine;
    }
    get spine() {
        return (this.renderer.isPortrait() && this.portraitSpine) ? this.portraitSpine : this.landscapeSpine;
    }
    getTrackEntry(track = 0) {
        return (this.renderer.isPortrait() && this.portraitTrackEntry[track]) ? this.portraitTrackEntry[track] : this.landscapeTrackEntry[track];
    }
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    create() {
        super.create();
        this.createSpineAnimation();
        this.stop();
        this.timeScale = this.uiProperties.timeScale;
        this.loop = [];
        this.processEvents = [];
        this.speedSetting = [];
        this.eventHandler = [];
        this.startHandler = [];
        this.completeHandler = [];
    }
    destroy() {
        super.destroy();
        DestroyUtil.destroy(this.landscapeSpine);
        DestroyUtil.destroy(this.portraitSpine);
        this.landscapeSpine = null;
        this.portraitSpine = null;
        this.landscapeTrackEntry = [];
        this.portraitTrackEntry = [];
        this.eventHandler = null;
        this.startHandler = null;
        this.completeHandler = null;
        this.processEvents = null;
        this.playing = null;
        this.loop = null;
        this.landscapeProperties = null;
        this.portraitProperties = null;
    }
    createSpineAnimation() {
        this.landscapeSpine = this.assetManager.getSpine(this.landscapeProperties, false, OrientationType.LANDSCAPE);
        this.landscapeSpine.state.addListener({
            complete: this.handleSpineComplete.bind(this),
            start: this.handleSpineStart.bind(this),
            event: this.handleSpineEvent.bind(this)
        });
        this.addChild(this.landscapeSpine);
        if (this.hasPortraitProperties) {
            this.portraitSpine = this.assetManager.getSpine(this.portraitProperties, false, OrientationType.PORTRAIT);
            this.portraitSpine.state.addListener({
                complete: this.handleSpineComplete.bind(this),
                start: this.handleSpineStart.bind(this),
                event: this.handleSpineEvent.bind(this)
            });
            this.addChild(this.portraitSpine);
            this.portraitSpine.renderable = this.renderer.isPortrait();
            this.landscapeSpine.renderable = this.renderer.isLandscape();
        }
    }
    stop() {
        this.loop = [];
        this.playing = false;
        this.landscapeTrackEntry = [];
        this.portraitTrackEntry = [];
        this.landscapeSpine.state.clearTracks();
        this.landscapeSpine.skeleton.setToSetupPose();
        if (this.landscapeSpine?.state?.tracks[0]) {
            this.landscapeSpine.state.addEmptyAnimation(0, 0, 0);
        }
        if (this.hasPortraitProperties) {
            this.portraitSpine.state.clearTracks();
            this.portraitSpine.skeleton.setToSetupPose();
            if (this.portraitSpine?.state.tracks[0]) {
                this.portraitSpine.state.addEmptyAnimation(0, 0, 0);
            }
        }
    }
    clearTrack(track, mixDuration = 0, delay = 0) {
        if (this.landscapeSpine?.state?.tracks[track]) {
            this.landscapeSpine.state.addEmptyAnimation(track, mixDuration, delay);
            this.landscapeTrackEntry[track] = null;
        }
        if (this.portraitSpine?.state?.tracks[track]) {
            this.portraitSpine.state.addEmptyAnimation(track, mixDuration, delay);
            this.portraitTrackEntry[track] = null;
        }
    }
    setSkin(name) {
        try {
            this.landscapeSpine.skeleton.setSkinByName(name);
            if (this.hasPortraitProperties) {
                this.portraitSpine.skeleton.setSkinByName(name);
            }
        }
        catch (e) {
            console.warn(e);
        }
    }
    setPosition(val) {
        if (Array.isArray(val)) {
            this.landscapeSpine.position.set(val[0].x, val[0].y);
            if (this.hasPortraitProperties) {
                this.portraitSpine.position.set(val[1].x, val[1].y);
            }
        }
        else {
            this.landscapeSpine.position.set(val.x, val.y);
            if (this.hasPortraitProperties) {
                this.portraitSpine.position.set(val.x, val.y);
            }
        }
    }
    offsetPosition(val) {
        let offset;
        if (Array.isArray(val)) {
            offset = val;
        }
        else {
            offset = [val, val];
        }
        this.setPosition([
            new PIXI.Point(this.landscapeSpine.x + offset[0].x, this.landscapeSpine.y + offset[0].y),
            new PIXI.Point(this.portraitSpine.x + offset[1].x, this.portraitSpine.y + offset[1].y)
        ]);
    }
    play(animation = SpineConstants.ANIMATION, loop = false, reset = true, track = 0) {
        try {
            this.processEvents[track] = true;
            this.loop[track] = loop;
            this.playing = true;
            if (reset) {
                if (this.landscapeSpine?.state?.tracks[track]) {
                    this.landscapeSpine.state.addEmptyAnimation(track, 0, 0);
                }
                if (this.portraitSpine?.state?.tracks[track]) {
                    this.portraitSpine.state.addEmptyAnimation(track, 0, 0);
                }
            }
            const landscapeAnim = Array.isArray(animation) ? animation[0] : animation;
            const portraitAnim = Array.isArray(animation) ? animation[1] : animation;
            this.landscapeTrackEntry[track] = this.landscapeSpine.state.setAnimation(track, landscapeAnim, loop);
            this.landscapeTrackEntry[track].isLandscape = true;
            if (this.hasPortraitProperties) {
                this.portraitTrackEntry[track] = this.portraitSpine.state.setAnimation(track, portraitAnim, loop);
                this.portraitTrackEntry[track].isLandscape = false;
            }
            else if (Array.isArray(animation)) {
                Logger.warn(`TitanSpine: Attempt to set portrait animation on TitanSpine "${this.name}" with no portrait properties. Please set forceResponsive = true in spine properties.`);
            }
            if (typeof this.timeScale === "number") {
                this.setSpeed(this.timeScale, track);
            }
            else if (Array.isArray(this.timeScale)) {
                this.setSpeed(this.timeScale[track], track);
            }
            this.speedSetting[track] = this.getTrackEntry(track).timeScale;
        }
        catch (e) {
            console.warn(e);
        }
    }
    gotoAndPlay(frame, animation = SpineConstants.ANIMATION, track = 0, loop = false) {
        try {
            this.processEvents[track] = false;
            const landscapeAnim = Array.isArray(animation) ? animation[0] : animation;
            const portraitAnim = Array.isArray(animation) ? animation[1] : animation;
            this.landscapeTrackEntry[track] = this.landscapeSpine.state.setAnimation(track, landscapeAnim, loop);
            this.landscapeTrackEntry[track].isLandscape = true;
            this.landscapeTrackEntry[track].trackTime = frame / 30;
            if (this.hasPortraitProperties) {
                this.portraitTrackEntry[track] = this.portraitSpine.state.setAnimation(track, portraitAnim, loop);
                this.portraitTrackEntry[track].isLandscape = false;
                this.portraitTrackEntry[track].trackTime = frame / 30;
            }
        }
        catch (e) {
            console.warn(e);
        }
    }
    gotoAndStop(frame, animation = SpineConstants.ANIMATION, track = 0, loop = false) {
        this.gotoAndPlay(frame, animation, track, loop);
        this.pause(track);
    }
    findBone(name) {
        return this.spine.skeleton.findBone(name);
    }
    findSlot(name) {
        return this.spine.skeleton.findSlot(name);
    }
    findAnimation(name) {
        return this.spine.spineData.findAnimation(name);
    }
    updateSkin(name) {
        this.spine.skeleton.setSkinByName(name);
    }
    updateSlot(name, sprite) {
        let slot = this.findSlot(name);
        slot.currentSprite.texture = sprite.texture;
    }
    pause(track = 0) {
        this.processEvents[track] = false;
        let temp = this.speedSetting[track] || 1;
        this.setSpeed(0, track);
        this.speedSetting[track] = temp;
    }
    resume(track = 0) {
        this.processEvents[track] = true;
        this.setSpeed(this.speedSetting[track], track);
    }
    setSpeed(value, track = 0) {
        this.speedSetting[track] = value;
        if (this.landscapeTrackEntry[track]) {
            this.landscapeTrackEntry[track].timeScale = value;
        }
        if (this.portraitTrackEntry[track]) {
            this.portraitTrackEntry[track].timeScale = value;
        }
    }
    setMix(duration, fromAnimName, toAnimName) {
        const landscapeFromAnim = Array.isArray(fromAnimName) ? fromAnimName[0] : fromAnimName;
        const portraitFromAnim = Array.isArray(fromAnimName) ? fromAnimName[1] : fromAnimName;
        const landscapeToAnim = Array.isArray(toAnimName) ? toAnimName[0] : toAnimName;
        const portraitToAnim = Array.isArray(toAnimName) ? toAnimName[1] : toAnimName;
        this.landscapeSpine.stateData.setMix(landscapeFromAnim, landscapeToAnim, duration);
        if (this.hasPortraitProperties) {
            this.portraitSpine.stateData.setMix(portraitFromAnim, portraitToAnim, duration);
        }
    }
    setSlotTexture(animation, slotName, texture) {
        const animations = Array.isArray(animation) ? animation : [animation, animation];
        const slotNames = Array.isArray(slotName) ? slotName : [slotName, slotName];
        const textures = Array.isArray(texture) ? texture : [texture, texture];
        this.landscapeSpine.state.setAnimation(0, animations[0], false);
        this.landscapeSpine.update(0);
        this.landscapeSpine.hackTextureBySlotName(slotNames[0], textures[0]);
        if (this.hasPortraitProperties) {
            this.portraitSpine.state.setAnimation(0, animations[1], false);
            this.portraitSpine.update(0);
            this.portraitSpine.hackTextureBySlotName(slotNames[1], textures[1]);
        }
    }
    handleSpineEvent(trackEntry, pixiEvent) {
        if (this.trackListenerShouldFire(trackEntry) && this.processEvents[trackEntry.trackIndex] && this.eventHandler[trackEntry.trackIndex]) {
            this.handleEvent(trackEntry, pixiEvent);
        }
    }
    handleSpineComplete(trackEntry) {
        if (this.trackListenerShouldFire(trackEntry) && !this.loop[trackEntry.trackIndex]) {
            this.handleComplete(trackEntry);
        }
    }
    handleSpineStart(trackEntry) {
        if (this.trackListenerShouldFire(trackEntry) && !this.loop[trackEntry.trackIndex]) {
            this.handleStart(trackEntry);
        }
    }
    handleComplete(trackEntry) {
        if (this.trackListenerShouldFire(trackEntry) && this.completeHandler[trackEntry.trackIndex]) {
            gsap.delayedCall(0.005, () => { this.callHandler(this.completeHandler[trackEntry.trackIndex], trackEntry); }); //Trigger the complete listener after spine has stopped animating to avoid retriggering if spine resumes on the final frame
        }
    }
    handleStart(trackEntry) {
        if (this.trackListenerShouldFire(trackEntry) && this.startHandler[trackEntry.trackIndex]) {
            this.callHandler(this.startHandler[trackEntry.trackIndex], trackEntry);
        }
    }
    handleEvent(trackEntry, pixiEvent) {
        if (this.trackListenerShouldFire(trackEntry) && this.eventHandler[trackEntry.trackIndex]) {
            this.callHandler(this.eventHandler[trackEntry.trackIndex], [trackEntry, pixiEvent]);
        }
    }
    trackListenerShouldFire(trackEntry, orientation) {
        if (!this.hasPortraitProperties) {
            return true;
        }
        const currentOrientation = orientation || this.renderer.getOrientation();
        const trackIsLandscape = trackEntry.isLandscape === true;
        const trackIsPortrait = trackEntry.isLandscape === false;
        return (trackIsLandscape && currentOrientation === OrientationType.LANDSCAPE) || (trackIsPortrait && currentOrientation === OrientationType.PORTRAIT);
    }
    isPlaying() {
        return this.playing;
    }
    isLooping(track = 0) {
        return this.loop[track];
    }
    getSpine(orientation) {
        if (orientation) {
            return orientation === OrientationType.PORTRAIT && this.portraitSpine ? this.portraitSpine : this.landscapeSpine;
        }
        return this.spine;
    }
    getState() {
        return this.spine.state;
    }
    getSkeleton() {
        return this.spine.skeleton;
    }
    getCurrentId() {
        return this.uiProperties.id;
    }
    onComplete(handler, scope, track = 0) {
        this.completeHandler[track] = new EventHandler(handler, scope);
    }
    onStart(handler, scope, track = 0) {
        this.startHandler[track] = new EventHandler(handler, scope);
    }
    onEvent(handler, scope, track = 0) {
        this.eventHandler[track] = new EventHandler(handler, scope);
    }
    removeCompleteHandlers() {
        this.completeHandler = [];
    }
    removeCompleteHandler(track = 0) {
        this.completeHandler[track] = null;
    }
    removeStartHandlers() {
        this.startHandler = [];
    }
    removeStartHandler(track = 0) {
        this.startHandler[track] = null;
    }
    removeEventHandlers() {
        this.eventHandler = [];
    }
    removeEventHandler(track = 0) {
        this.eventHandler[track] = null;
    }
    reset() {
        this.landscapeSpine.state.clearTracks();
        this.landscapeSpine.position.set(0, 0);
        this.landscapeSpine.scale.set(1, 1);
        this.landscapeSpine.rotation = 0;
        if (this.hasPortraitProperties) {
            this.portraitSpine.state.clearTracks();
            this.portraitSpine.position.set(0, 0);
            this.portraitSpine.scale.set(1, 1);
            this.portraitSpine.rotation = 0;
        }
    }
    playWithDelay(animation = SpineConstants.ANIMATION, loop = false) {
        this.resetDelays();
        this.hide();
        this.play(animation, loop);
        this.playDelay = this.callAfter(this.delay, this.show);
    }
    resetDelays() {
        DestroyUtil.destroy(this.playDelay);
        this.delay = null;
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.uiProperties = this.uiProperties;
        if (this.hasPortraitProperties && this.portraitSpine) {
            const data = event?.getCurrentTarget() || {};
            const isLandscape = this.renderer.isLandscape(data.forceOrientation);
            this.landscapeSpine.renderable = isLandscape;
            this.portraitSpine.renderable = !isLandscape;
        }
    }
}
TitanSpine.trackIdCounter = 0;
//# sourceMappingURL=TitanSpine.js.map